import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from 'antd';

import { v4 as uuidv4 } from 'uuid';
import refundAccountService from 'v2/services/refundAccount';
import uploadService from 'v2/services/upload';

interface IOwnProps {
  type: 'image' | 'file' | 'video';
  onChange: (name: string, id: number, imgPreview: any) => void;
  uploadType: string;
  label: string;
  labelSecondary?: string;
  fileName: string;
  onEvent: any;
  defaultPreview?: string;
  style?: any;
  className?: any;
  isRefundAccount?: boolean;
}

type IProps = IOwnProps;

const VIDEO_ACCEPTS = [
  'video/avi',
  'video/flv',
  'video/x-msvideo',
  'video/x-flv',
  'video/mp4',
  'video/quicktime',
  'video/x-ms-wmv',
  'video/x-matroska',
  'video/webm'
];

const FILE_ACCEPTS = [
  'application/pdf',
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv'
];

const IMAGE_ACCEPTS = ['image/jpeg', 'image/png', 'application/pdf'];

const MAX_IMAGE_SIZE_MB = 2;
const MAX_FILE_SIZE_MB = 2;
const MAX_VIDEO_SIZE_MB = 35;

const FileUploader: React.FC<IProps> = ({
  type,
  onChange,
  uploadType,
  label,
  labelSecondary,
  fileName,
  onEvent,
  defaultPreview = '',
  style,
  isRefundAccount
}) => {
  const [fileState, setFileState] = useState({
    name: '',
    size: '',
    type: '',
    error: '',
    imagePreview: defaultPreview
  });

  const shouldAcceptFile = useCallback(
    (fileType: string, size: number) => {
      const fileSizeMB = size / 1024 / 1024;

      if (type === 'image' && fileSizeMB > MAX_IMAGE_SIZE_MB) {
        return { shouldAcceptFile: false, message: 'Tamanho máximo: 2MB' };
      }

      if (type === 'file' && fileSizeMB > MAX_FILE_SIZE_MB) {
        return { shouldAcceptFile: false, message: 'Tamanho máximo: 2MB' };
      }

      if (type === 'video' && fileSizeMB > MAX_VIDEO_SIZE_MB) {
        return { shouldAcceptFile: false, message: 'Tamanho máximo: 35MB' };
      }

      if (type === 'image' && !IMAGE_ACCEPTS.includes(fileType)) {
        return { shouldAcceptFile: false, message: 'Extensões válidas: JPG, PNG ou PDF' };
      }

      if (type === 'video' && !VIDEO_ACCEPTS.includes(fileType)) {
        return { shouldAcceptFile: false, message: 'Extensões válidas: AVI, MP4, MKV, MOV, WMV ou WEBM' };
      }

      if (type === 'file' && !FILE_ACCEPTS.includes(fileType)) {
        return { shouldAcceptFile: false, message: 'Extensões válidas: PDF, PPT, CSV ou MSWORD' };
      }

      return { shouldAcceptFile: true, message: '' };
    },
    [type]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files[0];
      const reader = new FileReader();

      setFileState({
        name: '',
        size: '',
        type: '',
        imagePreview: '',
        error: ''
      });

      if (!file) {
        return;
      }

      const { shouldAcceptFile: isAccepted, message } = shouldAcceptFile(file.type, file.size);

      if (isAccepted) {
        reader.onloadend = () => {
          const url = reader.result;

          onEvent('uploading');

          (async () => {
            try {
              if (isRefundAccount) {
                const uploadId = await refundAccountService.uploadFile(file, uploadType);
                onChange(fileName, uploadId, url);
              } else {
                const upload = await uploadService.uploadFile(file, uploadType);
                const uploadId = upload.data.upload_id;
                onChange(fileName, uploadId, url);
              }
            } catch (error) {
              if (error?.response?.status === 401 && isRefundAccount) {
                toast.error('Este link está expirado, requisite um novo.');
                onEvent('error', 'Este link está expirado, requisite um novo.');
                return;
              }

              onEvent('error', 'Erro ao enviar arquivo. Tente novamente!');
            }
          })();

          setFileState({
            name: file.name,
            size: String(file.size),
            type: file.type,
            imagePreview: url as any,
            error: ''
          });
        };

        reader.readAsDataURL(file);
      } else {
        setFileState({
          name: '',
          size: '',
          type: '',
          imagePreview: null,
          error: message
        });
        toast.error(message);
      }
    },
    [shouldAcceptFile, onEvent, isRefundAccount, uploadType, onChange, fileName]
  );

  const inputFileID = uuidv4();
  const fileType = fileState.imagePreview?.split(':')[1]?.split(';')[0] || '';

  return (
    <div style={style}>
      <div className='container'>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          {fileState.type === 'application/pdf' && (
            <div style={{ marginRight: 16 }}>
              <svg x='0px' y='0px' viewBox='0 0 512 512' width='45px' height='45px'>
                <g>
                  <g>
                    <g>
                      <path
                        d='M494.479,138.557L364.04,3.018C362.183,1.09,359.621,0,356.945,0h-194.41c-21.757,0-39.458,17.694-39.458,39.442v137.789    H44.29c-16.278,0-29.521,13.239-29.521,29.513v147.744C14.769,370.761,28.012,384,44.29,384h78.787v88.627    c0,21.71,17.701,39.373,39.458,39.373h295.238c21.757,0,39.458-17.653,39.458-39.351V145.385    C497.231,142.839,496.244,140.392,494.479,138.557z M359.385,26.581l107.079,111.265H359.385V26.581z M44.29,364.308    c-5.42,0-9.828-4.405-9.828-9.82V206.744c0-5.415,4.409-9.821,9.828-9.821h265.882c5.42,0,9.828,4.406,9.828,9.821v147.744    c0,5.415-4.409,9.82-9.828,9.82H44.29z M477.538,472.649c0,10.84-8.867,19.659-19.766,19.659H162.535    c-10.899,0-19.766-8.828-19.766-19.68V384h167.403c16.278,0,29.521-13.239,29.521-29.512V206.744    c0-16.274-13.243-29.513-29.521-29.513H142.769V39.442c0-10.891,8.867-19.75,19.766-19.75h177.157v128    c0,5.438,4.409,9.846,9.846,9.846h128V472.649z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M132.481,249.894c-3.269-4.25-7.327-7.01-12.173-8.279c-3.154-0.846-9.923-1.269-20.308-1.269H72.596v84.577h17.077    v-31.904h11.135c7.731,0,13.635-0.404,17.712-1.212c3-0.654,5.952-1.99,8.856-4.01c2.904-2.019,5.298-4.798,7.183-8.336    c1.885-3.538,2.827-7.904,2.827-13.096C137.385,259.634,135.75,254.144,132.481,249.894z M117.856,273.173    c-1.288,1.885-3.067,3.269-5.337,4.154s-6.769,1.327-13.5,1.327h-9.346v-24h8.25c6.154,0,10.25,0.192,12.288,0.577    c2.769,0.5,5.058,1.75,6.865,3.75c1.808,2,2.712,4.539,2.712,7.615C119.789,269.096,119.144,271.288,117.856,273.173z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M219.481,263.452c-1.846-5.404-4.539-9.971-8.077-13.702s-7.789-6.327-12.75-7.789c-3.692-1.077-9.058-1.615-16.096-1.615    h-31.212v84.577h32.135c6.308,0,11.346-0.596,15.115-1.789c5.039-1.615,9.039-3.865,12-6.75c3.923-3.808,6.942-8.788,9.058-14.942    c1.731-5.039,2.596-11.039,2.596-18C222.25,275.519,221.327,268.856,219.481,263.452z M202.865,298.183    c-1.154,3.789-2.644,6.51-4.471,8.163c-1.827,1.654-4.125,2.827-6.894,3.519c-2.115,0.539-5.558,0.808-10.327,0.808h-12.75v0    v-56.019h7.673c6.961,0,11.635,0.269,14.019,0.808c3.192,0.692,5.827,2.019,7.904,3.981c2.077,1.962,3.692,4.692,4.846,8.192    c1.154,3.5,1.731,8.519,1.731,15.058C204.596,289.231,204.019,294.394,202.865,298.183z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <polygon
                        points='294.827,254.654 294.827,240.346 236.846,240.346 236.846,324.923 253.923,324.923 253.923,288.981     289.231,288.981 289.231,274.673 253.923,274.673 253.923,254.654   '
                        fill='#596375'
                      />
                    </g>
                  </g>
                </g>
              </svg>

              <div className='m-2'></div>
            </div>
          )}

          {fileState.type === 'application/msword' && (
            <div style={{ marginRight: 16 }}>
              <svg x='0px' y='0px' viewBox='0 0 512 512' width='45px' height='45px'>
                <g>
                  <g>
                    <g>
                      <path
                        d='M494.479,138.557L364.04,3.018C362.183,1.09,359.621,0,356.945,0h-194.41c-21.757,0-39.458,17.694-39.458,39.442v137.789    H44.29c-16.278,0-29.521,13.239-29.521,29.513v147.744C14.769,370.761,28.012,384,44.29,384h78.787v88.627    c0,21.71,17.701,39.373,39.458,39.373h295.238c21.757,0,39.458-17.653,39.458-39.351V145.385    C497.231,142.839,496.244,140.392,494.479,138.557z M359.385,26.581l107.079,111.265H359.385V26.581z M44.29,364.308    c-5.42,0-9.828-4.405-9.828-9.82V206.744c0-5.415,4.409-9.821,9.828-9.821h265.881c5.419,0,9.829,4.406,9.829,9.821v147.744    c0,5.415-4.41,9.82-9.829,9.82H44.29z M477.538,472.649c0,10.84-8.867,19.659-19.766,19.659H162.535    c-10.899,0-19.766-8.828-19.766-19.68V384h167.401c16.279,0,29.522-13.239,29.522-29.512V206.744    c0-16.274-13.243-29.513-29.522-29.513H142.769V39.442c0-10.891,8.867-19.75,19.766-19.75h177.157v128    c0,5.438,4.409,9.846,9.846,9.846h128V472.649z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M130.826,263.451c-1.846-5.403-4.536-9.969-8.076-13.701c-3.537-3.731-7.788-6.326-12.75-7.788    c-3.692-1.079-9.056-1.615-16.096-1.615h-31.21v84.576h32.133c6.309,0,11.348-0.595,15.116-1.79c5.039-1.615,9.039-3.865,12-6.749    c3.924-3.808,6.942-8.788,9.059-14.942c1.73-5.039,2.594-11.037,2.594-18.001C133.595,275.52,132.672,268.856,130.826,263.451z     M114.21,298.183c-1.152,3.789-2.644,6.508-4.47,8.162c-1.827,1.654-4.126,2.829-6.895,3.52    c-2.114,0.539-5.558,0.807-10.326,0.807H79.769v-56.02h7.672c6.964,0,11.636,0.269,14.021,0.808    c3.193,0.694,5.827,2.021,7.904,3.98c2.075,1.962,3.692,4.694,4.844,8.195c1.154,3.498,1.733,8.517,1.733,15.057    C115.944,289.231,115.365,294.395,114.21,298.183z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M294.097,293.826c-1.425,6.193-3.771,10.733-7.04,13.615c-3.269,2.885-7.173,4.328-11.712,4.328    c-6.154,0-11.153-2.27-14.998-6.806c-3.847-4.539-5.77-12.156-5.77-22.848c0-10.075,1.952-17.364,5.856-21.863    c3.904-4.502,8.99-6.753,15.259-6.753c4.539,0,8.394,1.27,11.567,3.808c3.173,2.54,5.261,6.001,6.26,10.385l16.904-4.04    c-1.922-6.769-4.807-11.96-8.652-15.576c-6.462-6.114-14.865-9.172-25.211-9.172c-11.848,0-21.406,3.894-28.674,11.683    c-7.269,7.789-10.905,18.72-10.905,32.797c0,13.307,3.616,23.798,10.848,31.471c7.229,7.673,16.46,11.51,27.692,11.51    c9.075,0,16.566-2.24,22.471-6.722c5.903-4.48,10.124-11.335,12.662-20.566L294.097,293.826z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M215.434,250.501c-7.483-7.732-17.452-11.597-29.915-11.597c-6.885,0-12.96,1.115-18.23,3.345    c-3.961,1.654-7.606,4.192-10.932,7.616c-3.328,3.421-5.952,7.288-7.877,11.596c-2.577,5.846-3.864,13.076-3.864,21.691    c0,13.462,3.712,24.03,11.133,31.702c7.424,7.673,17.426,11.51,30.002,11.51c12.423,0,22.346-3.854,29.77-11.567    c7.421-7.712,11.133-18.395,11.133-32.049C226.654,268.979,222.914,258.23,215.434,250.501z M202.452,304.414    c-4.364,4.903-9.952,7.355-16.761,7.355c-6.806,0-12.421-2.471-16.844-7.412c-4.423-4.943-6.636-12.202-6.636-21.779    c0-9.731,2.154-17.01,6.461-21.837c4.308-4.827,9.982-7.242,17.019-7.242c7.04,0,12.684,2.385,16.933,7.156    c4.251,4.768,6.375,12,6.375,21.691C208.999,292.155,206.818,299.51,202.452,304.414z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                </g>
              </svg>
              <div className='m-2'></div>
            </div>
          )}

          {(fileState.type === 'application/vnd.ms-powerpoint' || fileState.type === 'application/pptx') && (
            <div style={{ marginRight: 16 }}>
              <svg x='0px' y='0px' viewBox='0 0 512 512' width='45px' height='45px'>
                <g>
                  <g>
                    <g>
                      <path
                        d='M494.478,138.557L364.038,3.018C362.183,1.09,359.623,0,356.945,0H162.534c-21.757,0-39.457,17.694-39.457,39.442v137.789    H44.289c-16.276,0-29.519,13.239-29.519,29.513v147.744c0,16.273,13.243,29.512,29.519,29.512h78.788v88.627    c0,21.71,17.699,39.373,39.457,39.373h295.24c21.757,0,39.457-17.653,39.457-39.351V145.385    C497.231,142.839,496.245,140.392,494.478,138.557z M359.385,26.581l107.079,111.265H359.385V26.581z M44.289,364.308    c-5.418,0-9.827-4.405-9.827-9.82V206.744c0-5.415,4.409-9.821,9.827-9.821h265.882c5.421,0,9.829,4.406,9.829,9.821v147.744    c0,5.415-4.409,9.82-9.829,9.82H44.289z M477.538,472.649c0,10.84-8.865,19.659-19.764,19.659h-295.24    c-10.899,0-19.764-8.828-19.764-19.68V384h167.401c16.279,0,29.522-13.239,29.522-29.512V206.744    c0-16.274-13.243-29.513-29.522-29.513H142.769V39.442c0-10.891,8.865-19.75,19.764-19.75h177.159v128    c0,5.438,4.409,9.846,9.846,9.846h128V472.649z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <polygon
                        points='224.153,240.347 224.153,254.653 249.251,254.653 249.251,324.923 266.326,324.923 266.326,254.653     291.365,254.653 291.365,240.347   '
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M211.289,249.895c-3.269-4.251-7.328-7.01-12.172-8.281c-3.156-0.847-9.925-1.267-20.308-1.267h-27.405v84.576h17.076    v-31.904h11.136c7.729,0,13.635-0.404,17.711-1.211c3.001-0.655,5.952-1.992,8.856-4.01c2.902-2.019,5.297-4.798,7.183-8.337    c1.883-3.537,2.826-7.904,2.826-13.096C216.192,259.636,214.558,254.144,211.289,249.895z M196.665,273.174    c-1.29,1.883-3.07,3.269-5.337,4.153c-2.27,0.883-6.769,1.326-13.502,1.326h-9.346v-24h8.251c6.154,0,10.249,0.192,12.288,0.576    c2.769,0.502,5.058,1.75,6.865,3.751c1.81,1.999,2.713,4.539,2.713,7.616C198.597,269.095,197.952,271.288,196.665,273.174z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M132.48,249.895c-3.269-4.251-7.326-7.01-12.173-8.281c-3.153-0.847-9.922-1.267-20.308-1.267H72.595v84.576h17.078    v-31.904h11.133c7.732,0,13.635-0.404,17.711-1.211c3.001-0.655,5.952-1.992,8.857-4.01c2.904-2.019,5.297-4.798,7.183-8.337    c1.885-3.537,2.826-7.904,2.826-13.096C137.383,259.636,135.749,254.144,132.48,249.895z M117.856,273.174    c-1.29,1.883-3.067,3.269-5.337,4.153c-2.269,0.883-6.769,1.326-13.499,1.326h-9.347v-24h8.249c6.154,0,10.25,0.192,12.288,0.576    c2.769,0.502,5.058,1.75,6.865,3.751c1.81,1.999,2.713,4.539,2.713,7.616C119.789,269.096,119.144,271.288,117.856,273.174z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                </g>
              </svg>
              <div className='m-2'></div>
            </div>
          )}

          {fileState.imagePreview && type === 'video' && (
            <div style={{ border: '1px #ccc solid', borderRadius: 4, marginRight: 16 }}>
              <video controls style={{ width: 200, height: 200 }}>
                <source type={fileType} src={fileState.imagePreview} />
              </video>
            </div>
          )}

          {(fileState.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            fileState.type === 'application/vnd.ms-excel' ||
            fileState.type === 'text/csv') && (
            <div style={{ marginRight: 16 }}>
              <svg x='0px' y='0px' viewBox='0 0 512 512' width='45px' height='45px'>
                <g>
                  <g>
                    <g>
                      <path
                        d='M494.479,138.557L364.04,3.018C362.183,1.09,359.621,0,356.945,0h-194.41c-21.757,0-39.458,17.694-39.458,39.442v137.789    H44.29c-16.278,0-29.521,13.239-29.521,29.513v147.744C14.769,370.761,28.012,384,44.29,384h78.787v88.627    c0,21.71,17.701,39.373,39.458,39.373h295.238c21.757,0,39.458-17.653,39.458-39.351V145.385    C497.231,142.839,496.244,140.392,494.479,138.557z M359.385,26.581l107.079,111.265H359.385V26.581z M44.29,364.308    c-5.42,0-9.828-4.405-9.828-9.82V206.744c0-5.415,4.409-9.821,9.828-9.821h265.881c5.419,0,9.829,4.406,9.829,9.821v147.744    c0,5.415-4.41,9.82-9.829,9.82H44.29z M477.538,472.649c0,10.84-8.867,19.659-19.766,19.659H162.535    c-10.899,0-19.766-8.828-19.766-19.68V384h167.401c16.279,0,29.522-13.239,29.522-29.512V206.744    c0-16.274-13.243-29.513-29.522-29.513H142.769V39.442c0-10.891,8.867-19.75,19.766-19.75h177.157v128    c0,5.438,4.409,9.846,9.846,9.846h128V472.649z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d='M284.645,286.819c-2.25-3.636-5.366-6.501-9.346-8.598c-3.981-2.095-10.124-4.123-18.432-6.085    c-8.308-1.962-13.538-3.847-15.692-5.654c-1.694-1.423-2.54-3.137-2.54-5.135c0-2.193,0.903-3.944,2.713-5.251    c2.808-2.038,6.693-3.057,11.653-3.057c4.807,0,8.414,0.953,10.818,2.855c2.403,1.903,3.97,5.029,4.701,9.376l17.076-0.751    c-0.268-7.769-3.084-13.981-8.45-18.633c-5.367-4.655-13.356-6.981-23.97-6.981c-6.501,0-12.049,0.979-16.645,2.942    c-4.596,1.962-8.116,4.817-10.558,8.566c-2.442,3.751-3.665,7.781-3.665,12.089c0,6.69,2.597,12.364,7.791,17.019    c3.692,3.306,10.114,6.095,19.269,8.364c7.114,1.77,11.673,3.001,13.671,3.692c2.925,1.039,4.972,2.26,6.144,3.663    c1.174,1.406,1.76,3.106,1.76,5.108c0,3.114-1.394,5.836-4.182,8.162c-2.789,2.326-6.932,3.49-12.433,3.49    c-5.192,0-9.317-1.307-12.374-3.924c-3.057-2.614-5.085-6.71-6.087-12.288l-16.616,1.615c1.115,9.462,4.539,16.665,10.27,21.607    c5.731,4.94,13.942,7.412,24.635,7.412c7.345,0,13.482-1.029,18.405-3.087c4.923-2.055,8.728-5.201,11.421-9.433    c2.693-4.229,4.04-8.768,4.04-13.615C288.02,294.942,286.895,290.452,284.645,286.819z'
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <polygon
                        points='168.943,310.673 168.943,241.039 151.864,241.039 151.864,324.923 211.405,324.923 211.405,310.673   '
                        fill='#596375'
                      />
                    </g>
                  </g>
                  <g>
                    <g>
                      <polygon
                        points='113.73,281.423 140.039,240.347 120.251,240.347 103.636,267.52 86.673,240.347 66.713,240.347 92.903,280.788     64,324.923 84.48,324.923 103.289,295.672 122.038,324.923 142.634,324.923   '
                        fill='#596375'
                      />
                    </g>
                  </g>
                </g>
              </svg>

              <div className='m-2'></div>
            </div>
          )}

          {fileState.imagePreview &&
            type === 'image' &&
            fileType !== 'application/pdf' &&
            fileState.imagePreview.includes('image') && (
              <div style={{ border: '1px #ccc solid', borderRadius: 4, marginRight: 16 }}>
                <img
                  style={{ maxHeight: 150, maxWidth: 150 }}
                  className='img-preview'
                  src={fileState.imagePreview}
                  alt='fileuploaderimg'
                />
              </div>
            )}

          <div>
            <div>
              <span className='name__file' style={{ color: fileState.error ? '#dc3545' : '#000' }}>
                {label} {fileState.error ? ` - ${fileState.error}` : ''}
              </span>
              {labelSecondary && <span className='label-secondary'>{` - ${labelSecondary}`}</span>}
            </div>

            <div>
              <input
                style={{ display: 'none' }}
                className='input-file'
                id={inputFileID}
                type='file'
                onChange={handleChange}
              />
              <label htmlFor={inputFileID}>
                <span>
                  <Button type='primary' onClick={() => document.getElementById(inputFileID)?.click()}>
                    Selecionar Arquivo
                  </Button>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploader;
